import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../components/form/input/InputField";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { DatePicker } from "../../components/common/DatePicker";
import { CheckCircle } from "lucide-react";
import ListboxComponent from "../../components/common/ListboxComponent";
import {
  modalStyles,
  modalTitle,
  modalDescription,
  formStyles,
  label,
  input,
  errorMessage,
  closeButton,
  saveButton,
  saveButtonDisabled,
  successModalStyles,
  successModalCloseButton,
  successModalIconContainer,
  successModalHeading,
  successModalButton,
} from "../../styles/components/UserProfile/UserInfoCard.styles";

type TaskModalProps = {
  isOpen: boolean;
  onClose: () => void;
  state: {
    lead_id?: string;
    task_id?: string;
  };
  onTaskUpdated: (updatedTask: any) => void;
};

const EditTaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  state,
  onTaskUpdated,
}) => {
  const navigate = useNavigate();
  const [task_name, setTaskName] = useState("");
  const [task_description, setTaskDescription] = useState("");
  const [assigned_to, setAssignedTo] = useState("");
  const [due_date, setDueDate] = useState("");
  const [duration, setDuration] = useState("");
  const [start_datetime, setStartDateTime] = useState("");
  const [status_id, setStatusId] = useState("");
  const [saving, setSaving] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [errors, setErrors] = useState<{
    task_name?: string;
    task_description?: string;
    due_date?: string;
    duration?: string;
    start_datetime?: string;
    assigned_to?: string;
    status_id?: string;
  }>({});
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<
    { id: string; status_name: string }[]
  >([]);
  const { lead_id, task_id } = state;

  const token = sessionStorage.getItem("access_token");
  const userRoles = JSON.parse(sessionStorage.getItem("user_roles") || "[]");
  const isAdmin = userRoles.includes("Admin");

  const assignedToOptions = users.map((u) => ({ id: u.id, name: u.name }));
  const statusOptions = leadStatuses.map((s) => ({
    id: s.id,
    name: s.status_name,
  }));

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/name/list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }

        const data = await response.json();
        if (data.status === 200) {
          setUsers(data.data);
        } else {
          setErrors((prev) => ({ ...prev, assigned_to: "Failed to load users." }));
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setErrors((prev) => ({ ...prev, assigned_to: "Error loading users." }));
      }
    };

    if (token) {
      fetchUsers();
    }
  }, [token]);

  useEffect(() => {
    const fetchLeadStatuses = async () => {
      if (!token) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/status/name/list`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch lead statuses");

        const result = await response.json();
        if (result.status === 200) {
          setLeadStatuses(result.data);
        } else {
          setErrors((prev) => ({ ...prev, status_id: "Failed to load statuses." }));
        }
      } catch (error) {
        console.error("Error fetching lead statuses:", error);
        setErrors((prev) => ({ ...prev, status_id: "Error loading statuses." }));
      }
    };

    if (token) {
      fetchLeadStatuses();
    }
  }, [token]);

  const fetchTaskDetails = useCallback(async () => {
    if (!token || !task_id) return;

    try {
      const formData = new FormData();
      formData.append("task_id", task_id);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/task/edit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Failed to fetch task details");

      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data) && result.data.length > 0) {
        const task = result.data[0];
        setTaskName(task.task_name || "");
        setTaskDescription(task.task_description || "");
        setAssignedTo(task.assigned_to || "");
        setDueDate(task.due_date || "");
        setDuration(task.duration || "");
        setStartDateTime(task.start_datetime || "");
        setStatusId(task.status || "");
      } else {
        setErrors((prev) => ({ ...prev, task_name: "Failed to fetch task details." }));
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
      setErrors((prev) => ({ ...prev, task_name: "Error fetching task details." }));
    }
  }, [token, task_id]);

  useEffect(() => {
    if (isOpen && task_id) {
      fetchTaskDetails();
    }
  }, [isOpen, task_id, fetchTaskDetails]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof errors
  ) => {
    const value = e.target.value;
    switch (field) {
      case "task_name":
        setTaskName(value);
        break;
      case "task_description":
        setTaskDescription(value);
        break;
      case "due_date":
        setDueDate(value);
        break;
      case "duration":
        setDuration(value);
        break;
      case "start_datetime":
        setStartDateTime(value);
        break;
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const newErrors: typeof errors = {};

    if (!task_name.trim()) newErrors.task_name = "Task name field is required.";
    if (!task_description.trim()) newErrors.task_description = "Task description field is required.";
    if (!due_date) newErrors.due_date = "Due date field is required.";
    if (!duration) {
      newErrors.duration = "Duration field is required.";
    } else if (isNaN(Number(duration)) || Number(duration) <= 0) {
      newErrors.duration = "Duration field must be a positive number.";
    }
    if (!start_datetime) newErrors.start_datetime = "Start date field is required.";
    if (!status_id) newErrors.status_id = "Status field is required.";

    // Validate due_date >= start_datetime
    if (due_date && start_datetime) {
      const start = new Date(start_datetime.split("-").reverse().join("-"));
      const due = new Date(due_date.split("-").reverse().join("-"));
      if (due < start) {
        newErrors.due_date = "Due date field must be after start date field.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    if (!token) {
      setErrors({ ...newErrors, assigned_to: "Authorization required. Please log in." });
      navigate("/signin");
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("task_name", task_name);
    formData.append("task_description", task_description);
    formData.append("assigned_to", assigned_to);
    formData.append("due_date", due_date);
    formData.append("duration", duration);
    formData.append("start_datetime", start_datetime);
    formData.append("lead_id", lead_id ?? "");
    formData.append("status_id", status_id);
    formData.append("task_id", task_id ?? "");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/task/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        onClose();
        setTimeout(() => {
          setSuccessModalOpen(false);
          onTaskUpdated(response.data.data);
        }, 2000);
      } else {
        setErrors({ ...newErrors, task_name: "Unexpected server response." });
      }
    } catch (error: any) {
      console.error("API Error:", error.response?.data || error.message);
      setErrors({
        ...newErrors,
        task_name:
          error.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className={modalStyles}>
        <h4 className={modalTitle}>Edit Lead Task</h4>
        <p className={modalDescription}>
          Update the details to modify the task.
        </p>
        <form onSubmit={handleSubmit} className={formStyles}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative">
              <label className={label}>
                Task Name <span className="text-error-500 dark:text-red-400">*</span>
              </label>
              <Input
                type="text"
                value={task_name}
                onChange={(e) => handleChange(e, "task_name")}
                className={`${input} ${
                  errors.task_name
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
              {errors.task_name && <p className={errorMessage}>{errors.task_name}</p>}
            </div>
            <div className="relative">
              <label className={label}>
                Duration (in minutes) <span className="text-error-500 dark:text-red-400">*</span>
              </label>
              <Input
                type="text"
                name="duration"
                value={duration}
                onChange={(e) => handleChange(e, "duration")}
                numericOnly={true}
                className={`${input} ${
                  errors.duration
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
              {errors.duration && <p className={errorMessage}>{errors.duration}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative">
              <DatePicker
                value={start_datetime}
                onChange={(dates) => {
                  const date = dates[0];
                  if (date) {
                    const dd = String(date.getDate()).padStart(2, "0");
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const yyyy = date.getFullYear();
                    setStartDateTime(`${dd}-${mm}-${yyyy}`);
                    setErrors((prev) => ({ ...prev, start_datetime: undefined }));
                  } else {
                    setStartDateTime("");
                    setErrors((prev) => ({ ...prev, start_datetime: undefined }));
                  }
                }}
                label="Start Date"
                required
                error={errors.start_datetime}
                className={`${input} ${
                  errors.start_datetime
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
            </div>
            <div className="relative">
              <DatePicker
                value={due_date}
                onChange={(dates) => {
                  const date = dates[0];
                  if (date) {
                    const dd = String(date.getDate()).padStart(2, "0");
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const yyyy = date.getFullYear();
                    setDueDate(`${dd}-${mm}-${yyyy}`);
                    setErrors((prev) => ({ ...prev, due_date: undefined }));
                  } else {
                    setDueDate("");
                    setErrors((prev) => ({ ...prev, due_date: undefined }));
                  }
                }}
                label="Due Date"
                required
                error={errors.due_date}
                className={`${input} ${
                  errors.due_date
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            {isAdmin && (
              <div className="flex-1">
                <ListboxComponent
                  value={assignedToOptions.find((opt) => opt.id === assigned_to) || null}
                  onChange={(selected) => {
                    const val = selected?.id || "";
                    setAssignedTo(val);
                    setErrors((prev) => ({ ...prev, assigned_to: undefined }));
                  }}
                  items={assignedToOptions}
                  label="Assigned To"
                  displayKey="name"
                  className={`${input} ${
                    errors.assigned_to
                      ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                      : ""
                  }`}
                />
                {errors.assigned_to && (
                  <p className={errorMessage}>{errors.assigned_to}</p>
                )}
              </div>
            )}
            <div className={isAdmin ? "flex-1" : "w-full"}>
              <ListboxComponent
                value={statusOptions.find((opt) => opt.id === status_id) || null}
                onChange={(selected) => {
                  const val = selected?.id || "";
                  setStatusId(val);
                  setErrors((prev) => ({
                    ...prev,
                    status_id: val ? undefined : "Status field is required.",
                  }));
                }}
                items={statusOptions}
                label="Status"
                displayKey="name"
                required
                error={errors.status_id}
                className={`${input} ${
                  errors.status_id
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                }`}
              />
            </div>
          </div>
          <div className="relative">
            <label className={label}>
              Task Description <span className="text-error-500 dark:text-red-400">*</span>
            </label>
            <textarea
              value={task_description}
              onChange={(e) => handleChange(e, "task_description")}
              rows={4}
              className={`${input} min-h-[5rem] ${
                errors.task_description
                  ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                  : ""
              }`}
            />
            {errors.task_description && (
              <p className={errorMessage}>{errors.task_description}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 items-center">
            <div className="flex-1 flex gap-4 justify-center sm:justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`${saveButton} ${saving ? saveButtonDisabled : ""}`}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button type="button" onClick={onClose} className={closeButton}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className={successModalStyles}
      >
        <button
          onClick={() => setSuccessModalOpen(false)}
          className={successModalCloseButton}
        />
        <div className="flex justify-center">
          <div className={successModalIconContainer}>
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className={successModalHeading}>Task Updated Successfully</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          The task has been updated in the system successfully.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className={successModalButton}
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default EditTaskModal;